import React from 'react';
import { AlertIcon, CheckCircleIcon } from '../Icons';

const formatLabel = (value) => String(value || '-').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const buttonStyle = (variant) => {
  if (variant === 'danger') {
    return {
      borderColor: '#fecaca',
      backgroundColor: '#fff1f2',
      color: '#b91c1c'
    };
  }
  return {};
};

const fieldLabelStyle = {
  display: 'block',
  marginBottom: '0.4rem',
  fontSize: '0.78rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase'
};

const pillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: '28px',
  padding: '0.25rem 0.6rem',
  border: '1px solid var(--border-light)',
  borderRadius: '9999px',
  backgroundColor: 'var(--bg-soft)',
  color: 'var(--text-secondary)',
  fontSize: '0.74rem',
  fontWeight: 800
};

const noticeStyle = (type = 'success') => ({
  marginBottom: '1rem',
  padding: '0.85rem 1rem',
  borderRadius: '8px',
  border: `1px solid ${type === 'success' ? 'rgba(16, 185, 129, 0.35)' : 'rgba(239, 68, 68, 0.35)'}`,
  backgroundColor: type === 'success' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
  color: type === 'success' ? 'var(--success)' : 'var(--error)',
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  fontSize: '0.875rem',
  fontWeight: 700
});

const assigneeAvatarStyle = (selected = false) => ({
  width: '28px',
  height: '28px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
  backgroundColor: selected ? 'var(--primary)' : 'rgba(var(--primary-rgb), 0.1)',
  color: selected ? '#fff' : 'var(--primary)',
  fontSize: '0.72rem',
  fontWeight: 900
});

const getInitials = (value) => {
  const text = String(value || '').trim();
  if (!text) return '?';
  const parts = text.split(/\s+/).slice(0, 2);
  return parts.map(part => part[0]).join('').toUpperCase();
};

const WorkflowActionPanel = ({
  actions = [],
  selectedAction,
  selectedActionKey,
  onActionChange,
  selectedActionDisabledReason,
  editing = false,
  previousOwners = [],
  selectedActionNeedsAssignee,
  selectedActionNeedsNote,
  selectedActionNeedsClosePermission,
  selectedActionCanSelectRoute,
  showRouteFields,
  statusOptions = [],
  workingStatusesOnly = false,
  routeStatusValue,
  previewStatusLabel,
  canCloseRoute = false,
  formatRouteLabel = formatLabel,
  onStatusChange,
  currentOwnerLabel = 'Unassigned',
  previewOwnerLabel = 'Unassigned',
  showAssigneeField,
  assigneeHelperText,
  canAssignToMe,
  currentUserId,
  selectedAssignee,
  onAssigneeChange,
  assigneeSearch,
  onAssigneeSearchChange,
  roleOptions = [],
  assigneeRoleFilter,
  onAssigneeRoleFilterChange,
  filteredAssignees = [],
  noteValue,
  onNoteChange,
  canWriteNote,
  actionNotice,
  actionError,
  canRunWorkflow,
  canAssignTicket,
  savingAction,
  submitDisabled,
  submitLabel,
  onSubmit,
  onClose
}) => {
  const showRequirements = selectedActionNeedsNote || selectedActionNeedsClosePermission;
  return (
  <section className="card ticket-content-card ticket-workflow-panel" aria-label="Ticket assignment">
    <div className="ticket-section-heading"><h3>{actions.some(action => action.key === 'assign') ? 'Assign / hand off' : 'Workflow'}</h3>
      {onClose && <button type="button" className="btn btn-secondary" disabled={!!savingAction} onClick={onClose}>Close</button>}
    </div>
    {actionNotice && (
      <div role="status" style={noticeStyle(actionNotice.type)}>
        <CheckCircleIcon size={16} />
        <span>{actionNotice.message}</span>
      </div>
    )}

    {actionError && (
      <div role="alert" style={noticeStyle('error')}>
        <AlertIcon size={16} />
        <span>{actionError}</span>
      </div>
    )}

    {!canRunWorkflow && actions.some(action => action.type === 'workflow') && (
      <div style={{ marginBottom: '1rem', border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.75rem 0.9rem', backgroundColor: 'var(--bg-soft)', color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.45 }}>
        {canAssignTicket
          ? 'You can reassign this item, but only the current owner can change status.'
          : 'Only the current owner can change status.'}
      </div>
    )}

    {actions.length === 0 ? (
      <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No action is available for your permission level.</div>
    ) : (
      <div className="ticket-composer-body">
          {actions.length > 1 && <div className="ticket-composer-actions" role="group" aria-label="Ticket action">
            {actions.map(action => (
              <button key={action.key} type="button" className={`btn ${selectedActionKey === action.key ? 'btn-primary' : 'btn-secondary'}`}
                aria-pressed={selectedActionKey === action.key} disabled={!!savingAction || !!action.disabled_reason}
                title={action.disabled_reason || action.description}
                onClick={() => onActionChange(action.key)}>{action.label}</button>
            ))}
          </div>}

          {editing && <p className="ticket-inline-notice">Save or cancel configuration edits before changing owner or status.</p>}
          <fieldset className="ticket-composer-fields" disabled={!!savingAction || (editing)}>

          {showRequirements && <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {selectedActionNeedsAssignee && <span style={pillStyle}>Assignee required</span>}
              {selectedActionNeedsNote && <span style={pillStyle}>Reason required</span>}
              {selectedActionNeedsClosePermission && <span style={pillStyle}>Close permission</span>}
            </div>
          </div>}

          {selectedActionDisabledReason && <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{selectedActionDisabledReason}</div>}

          {showRouteFields && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem', alignItems: 'start' }}>
              {selectedAction?.next_stage && (
                <div>
                  <div style={fieldLabelStyle}>Next step</div>
                  <div className="input" style={{ backgroundColor: 'var(--bg-soft)' }}>{selectedAction.next_stage_label || formatRouteLabel(selectedAction.next_stage)}</div>
                </div>
              )}
              <div>
                <label style={fieldLabelStyle}>{workingStatusesOnly ? 'Working status' : 'Status'}</label>
                <select aria-label="Working status" className="input" value={routeStatusValue || ''} disabled={!selectedActionCanSelectRoute || !!savingAction} onChange={(e) => onStatusChange(e.target.value)}>
                  {!statusOptions.some(status => status.value === routeStatusValue) && routeStatusValue && <option value={routeStatusValue}>{previewStatusLabel}</option>}
                  {statusOptions.map(status => (
                    <option key={status.value} value={status.value} disabled={(status.is_active === false && status.value !== routeStatusValue) || (['completed', 'cancelled'].includes(status.value) && !canCloseRoute)}>
                      {status.label || formatRouteLabel(status.value)}
                    </option>
                  ))}
                </select>
                {workingStatusesOnly && <p className="ticket-muted">Select one of the four working statuses. Approval, rejection and completion use their separate actions.</p>}
              </div>
              {!showAssigneeField && <div>
                <div style={fieldLabelStyle}>Owner</div>
                <div style={{ minHeight: '42px', border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.5rem 0.7rem', backgroundColor: 'var(--bg-soft)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.7rem' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem', fontWeight: 700 }}>{currentOwnerLabel} →</span>
                  <strong style={{ color: selectedActionNeedsAssignee && !selectedAssignee ? 'var(--warning)' : 'var(--text-primary)', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewOwnerLabel}</strong>
                </div>
              </div>}
            </div>
          )}

          {showAssigneeField && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', alignItems: 'baseline', marginBottom: '0.55rem' }}>
                <label htmlFor="ticket-next-owner" style={{ ...fieldLabelStyle, marginBottom: 0 }}>{selectedAction?.key === 'assign' ? 'Owner' : 'Next assignee'}</label>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{assigneeHelperText}</div>
              </div>

              {roleOptions.length > 0 && (
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.55rem' }}>
                  {['all', ...roleOptions].map(role => {
                    const selected = role === assigneeRoleFilter;
                    return (
                      <button
                        key={role}
                        type="button"
                        className="btn"
                        style={{ padding: '0.35rem 0.65rem', fontSize: '0.75rem', borderColor: selected ? 'var(--primary)' : 'var(--border-light)', backgroundColor: selected ? 'rgba(var(--primary-rgb), 0.08)' : 'var(--bg-card)', color: selected ? 'var(--primary)' : 'var(--text-secondary)' }}
                        disabled={!!savingAction}
                        onClick={() => onAssigneeRoleFilterChange(role)}
                      >
                        {role === 'all' ? 'All roles' : formatLabel(role)}
                      </button>
                    );
                  })}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.25rem', alignItems: 'flex-start' }}>
                <div style={{ position: 'relative', flex: '1 1 260px' }}>
                  <input id="ticket-next-owner" className="input" value={assigneeSearch} onChange={(e) => onAssigneeSearchChange(e.target.value)} placeholder="Search assignee by username, email, or role..." disabled={!!savingAction} />
                  {assigneeSearch.trim() && assigneeSearch.trim().toLowerCase() !== String(selectedAssignee?.username || '').toLowerCase() && assigneeSearch.trim().toLowerCase() !== String(selectedAssignee?.email || '').toLowerCase() && (
                    <div className="ticket-owner-results">
                      {filteredAssignees.slice(0, 10).length === 0 ? (
                        <div style={{ padding: '0.65rem 0.75rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>No active assignee matches this search.</div>
                      ) : filteredAssignees.slice(0, 10).map(user => {
                        const selected = user._id === selectedAssignee?._id;
                        return (
                          <button
                            key={user._id}
                            type="button"
                            disabled={!!savingAction}
                            onClick={() => onAssigneeChange(user._id)}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minHeight: '38px', padding: '0.45rem 0.55rem', borderRadius: '6px', border: `1px solid ${selected ? 'var(--primary)' : 'transparent'}`, backgroundColor: selected ? 'rgba(var(--primary-rgb), 0.08)' : 'transparent', color: 'var(--text-primary)', cursor: savingAction ? 'not-allowed' : 'pointer', textAlign: 'left' }}
                          >
                            <span style={assigneeAvatarStyle(selected)}>{getInitials(user.username || user.email)}</span>
                            <span style={{ minWidth: 0, display: 'grid', gap: '0.02rem' }}>
                              <span style={{ fontWeight: 900, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.username}</span>
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatLabel(user.role)}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {canAssignToMe && (
                  <button type="button" className="btn btn-secondary" style={{ minHeight: '42px' }} disabled={!!savingAction} onClick={() => onAssigneeChange(currentUserId)}>
                    Assign to me
                  </button>
                )}
              </div>
              {previousOwners.length > 0 && <div className="ticket-previous-owners">
                <span>Previous owners</span>
                {previousOwners.map(user => <button key={user._id} type="button" className="btn btn-secondary"
                  onClick={() => onAssigneeChange(user._id)}>{user.username || user.email}</button>)}
              </div>}
            </div>
          )}

          {selectedActionNeedsNote && (
            <div>
              <label htmlFor="ticket-workflow-reason" style={fieldLabelStyle}>Reason *</label>
              <textarea id="ticket-workflow-reason" className="input" rows="2" required value={noteValue}
                onChange={event => onNoteChange(event.target.value)} placeholder="Explain the reason for this action..." disabled={!canWriteNote || !!savingAction} />
            </div>
          )}

          <div className="ticket-approval-actions">
            <button type="button" className={`btn ${selectedAction?.variant === 'primary' || selectedActionKey === 'assign' ? 'btn-primary' : 'btn-secondary'}`} style={buttonStyle(selectedAction?.variant)} disabled={submitDisabled} onClick={onSubmit} title={selectedActionDisabledReason}>
              {savingAction && savingAction !== 'commented' ? 'Saving...' : submitLabel}
            </button>
          </div>
          </fieldset>
      </div>
    )}
  </section>
  );
};

export default WorkflowActionPanel;
